import { printHtml } from "./print";

const COMPANY_INFO_KEY = "almoune_company_info";
const defaultCompanyInfo = {
  companyName: "ALMOUNE",
  address: "",
  phone: "",
  siret: "",
  tvaIntracom: "",
  email: ""
};

export const getCompanyInfo = () => {
  if (typeof window === "undefined") return { ...defaultCompanyInfo };
  try {
    const raw = window.localStorage.getItem(COMPANY_INFO_KEY);
    if (!raw) return { ...defaultCompanyInfo };
    return { ...defaultCompanyInfo, ...JSON.parse(raw) };
  } catch {
    return { ...defaultCompanyInfo };
  }
};

export const saveCompanyInfo = (info) => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(COMPANY_INFO_KEY, JSON.stringify(info));
  }
  return info;
};

const formatMoney = (val) => Number(val).toFixed(2) + " €";

const baseStyles = `
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: #333;
  line-height: 1.5;
  margin: 0;
  padding: 20px;
`;

const getDocumentShell = (content) => `
  <div style="${baseStyles}">
    ${content}
  </div>
`;

const buildHeader = (title, documentRef, dateRef, companyInfo, supplier) => {
  return `
    <div style="display: flex; justify-content: space-between; margin-bottom: 40px;">
      <div style="width: 45%;">
        <h2 style="margin: 0 0 10px 0; font-size: 20px;">${companyInfo.companyName || "ALMOUNE"}</h2>
        <div style="color: #555;">
          ${companyInfo.address ? companyInfo.address.replace(/\n/g, '<br/>') + '<br/>' : ''}
          ${companyInfo.phone ? 'Tél : ' + companyInfo.phone + '<br/>' : ''}
          ${companyInfo.email ? 'Email : ' + companyInfo.email + '<br/>' : ''}
          ${companyInfo.siret ? 'SIRET : ' + companyInfo.siret + '<br/>' : ''}
          ${companyInfo.tvaIntracom ? 'TVA Intra : ' + companyInfo.tvaIntracom : ''}
        </div>
      </div>
      <div style="width: 45%; text-align: right;">
        <h1 style="margin: 0 0 10px 0; font-size: 24px; color: #000; text-transform: uppercase;">${title}</h1>
        <div style="margin-bottom: 20px;">
          <strong>Réf :</strong> ${documentRef}<br/>
          <strong>Date :</strong> ${dateRef}
        </div>
        <div style="text-align: left; border: 1px solid #000; padding: 15px;">
          <h3 style="margin: 0 0 5px 0; font-size: 14px;">DESTINATAIRE</h3>
          <strong>${supplier.name || ""}</strong><br/>
          ${supplier.address ? supplier.address.replace(/\n/g, '<br/>') + '<br/>' : ''}
          ${supplier.phone ? 'Tél : ' + supplier.phone + '<br/>' : ''}
          ${supplier.email ? 'Email : ' + supplier.email : ''}
        </div>
      </div>
    </div>
  `;
};

const buildItemsTable = (items) => {
  let rows = items.map(item => `
    <tr>
      <td style="border: 1px solid #000; padding: 5px;">${item.productId || item.id || ""}</td>
      <td style="border: 1px solid #000; padding: 5px;">${item.productName || item.name || ""}</td>
      <td style="border: 1px solid #000; padding: 5px; text-align: right; font-family: monospace;">${item.quantity || 0}</td>
      <td style="border: 1px solid #000; padding: 5px; text-align: right; font-family: monospace;">${formatMoney(item.unitCost || item.costPrice || item.purchasePrice || 0)}</td>
      <td style="border: 1px solid #000; padding: 5px; text-align: right; font-family: monospace;">${((item.tvaRate || 0) * 100).toFixed(1)}%</td>
      <td style="border: 1px solid #000; padding: 5px; text-align: right; font-family: monospace;">${formatMoney(item.lineTotal || ((item.quantity || 0) * (item.unitCost || item.costPrice || item.purchasePrice || 0)))}</td>
    </tr>
  `).join("");

  return `
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
      <thead>
        <tr style="background-color: #f5f5f5;">
          <th style="border: 1px solid #000; padding: 5px; text-align: left; width: 15%;">Réf</th>
          <th style="border: 1px solid #000; padding: 5px; text-align: left; width: 40%;">Désignation</th>
          <th style="border: 1px solid #000; padding: 5px; text-align: right; width: 10%;">Qté</th>
          <th style="border: 1px solid #000; padding: 5px; text-align: right; width: 15%;">P.U. HT</th>
          <th style="border: 1px solid #000; padding: 5px; text-align: right; width: 10%;">TVA</th>
          <th style="border: 1px solid #000; padding: 5px; text-align: right; width: 10%;">Total HT</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>
  `;
};

const buildTotals = (items) => {
  let totalHT = 0;
  const tvaGroups = {};

  items.forEach(item => {
    const unitCost = item.unitCost || item.costPrice || item.purchasePrice || 0;
    const qty = item.quantity || 0;
    const lineTotal = item.lineTotal || (qty * unitCost);
    totalHT += lineTotal;
    
    const rate = item.tvaRate || 0;
    if (!tvaGroups[rate]) tvaGroups[rate] = 0;
    tvaGroups[rate] += lineTotal * rate;
  });

  let totalTVA = 0;
  const tvaRows = Object.keys(tvaGroups).sort().map(rate => {
    const rateVal = parseFloat(rate);
    const amount = tvaGroups[rate];
    totalTVA += amount;
    return `
      <tr>
        <td style="padding: 5px; text-align: left; border: 1px solid #000;">TVA ${(rateVal * 100).toFixed(1)}%</td>
        <td style="padding: 5px; text-align: right; font-family: monospace; border: 1px solid #000;">${formatMoney(amount)}</td>
      </tr>
    `;
  }).join('');

  const totalTTC = totalHT + totalTVA;

  return `
    <div style="display: flex; justify-content: flex-end;">
      <table style="width: 40%; border-collapse: collapse; border: 1px solid #000;">
        <tr>
          <td style="padding: 5px; text-align: left; border: 1px solid #000;"><strong>Total HT</strong></td>
          <td style="padding: 5px; text-align: right; font-family: monospace; border: 1px solid #000;"><strong>${formatMoney(totalHT)}</strong></td>
        </tr>
        ${tvaRows}
        <tr style="background-color: #f5f5f5;">
          <td style="padding: 5px; text-align: left; border: 1px solid #000;"><strong>Total TTC</strong></td>
          <td style="padding: 5px; text-align: right; font-family: monospace; border: 1px solid #000;"><strong>${formatMoney(totalTTC)}</strong></td>
        </tr>
      </table>
    </div>
  `;
};

const buildFooter = (paymentTerms, notes) => {
  return `
    <div style="margin-top: 40px; font-size: 11px;">
      ${paymentTerms ? "<p><strong>Conditions de règlement :</strong> " + paymentTerms + "</p>" : ""}
      ${notes ? "<p><strong>Notes :</strong><br/>" + notes.replace(/\n/g, '<br/>') + "</p>" : ""}
      <div style="margin-top: 50px; border-top: 1px dashed #000; padding-top: 10px; width: 250px;">
        <strong>Signature / Cachet :</strong>
      </div>
    </div>
  `;
};

export const buildBonDeCommandeHtml = (purchase, supplier, companyInfo) => {
  const content = `
    ${buildHeader("BON DE COMMANDE", purchase.orderNumber || purchase.id, new Date().toLocaleDateString('fr-FR'), companyInfo, supplier)}
    <div style="margin-bottom: 20px;">
      <strong>Date de livraison souhaitée :</strong> ${purchase.expectedDeliveryDate || "À définir"}
    </div>
    ${buildItemsTable(purchase.items || [])}
    ${buildTotals(purchase.items || [])}
    ${buildFooter(supplier.paymentTerms, purchase.notes)}
  `;
  return getDocumentShell(content);
};

export const buildFactureFournisseurHtml = (invoice, supplier, companyInfo) => {
  const statusHtml = `
    <div style="margin-bottom: 20px;">
      <strong>Statut de la facture :</strong> <span style="padding: 3px 8px; border: 1px solid #000; text-transform: uppercase;">${invoice.status || "En attente"}</span><br/>
      ${invoice.dueDate ? "<strong>Date d'échéance :</strong> " + new Date(invoice.dueDate).toLocaleDateString('fr-FR') : ""}
    </div>
  `;
  const content = `
    ${buildHeader("FACTURE FOURNISSEUR", invoice.invoiceNumber || invoice.id, new Date(invoice.invoiceDate).toLocaleDateString('fr-FR'), companyInfo, supplier)}
    ${statusHtml}
    ${buildItemsTable(invoice.items || [])}
    ${buildTotals(invoice.items || [])}
    ${buildFooter(supplier.paymentTerms, invoice.notes)}
  `;
  return getDocumentShell(content);
};

export const printBonDeCommande = (purchase, supplier, companyInfo) => {
  const html = buildBonDeCommandeHtml(purchase, supplier, companyInfo);
  printHtml(html);
};

export const printFactureFournisseur = (invoice, supplier, companyInfo) => {
  const html = buildFactureFournisseurHtml(invoice, supplier, companyInfo);
  printHtml(html);
};
